import type {
  DrydockAudit,
  DrydockCloseout,
  DrydockJobList,
  DrydockLibrarySource,
  DrydockProject,
  DrydockProjectSource,
  DrydockProjects,
  DrydockScope,
  DrydockScopeSource,
  DrydockSettings,
  DrydockSettingsSource,
  PdiDefaultReport,
  PdiInspection,
  PdiInspectionSource,
  PdiInspectionWork,
  PdiLibrary,
  PdiNode,
  PdiPublishResult,
  PdiVersion,
} from '@felix/ui';
import { api, officeApi } from './api.js';
import { officeBrand } from './drills-source.js';

const BASE = '/office/drydock';
const enc = encodeURIComponent;

/**
 * Pre-Docking kütüphanesi (ADR 0129 §2, §3): şirket verisi, ofiste yazılır ve yayımlanır;
 * `drydock_library` yeteneğini bildiren gemiye iner.
 */
export const officeDrydockLibrarySource: DrydockLibrarySource = {
  scope: 'office',
  load: () => api<PdiLibrary>(`${BASE}/library`),
  version: (id) => api<PdiVersion>(`${BASE}/library/versions/${enc(id)}`),
  saveNode: ({ id, rowVersion, node }) =>
    id
      ? api<PdiNode>(`${BASE}/library/nodes/${enc(id)}`, {
          method: 'PATCH',
          body: { rowVersion, node },
        })
      : api<PdiNode>(`${BASE}/library/nodes`, { method: 'POST', body: { node } }),
  publish: (note) =>
    api<PdiPublishResult>(`${BASE}/library/publish`, { method: 'POST', body: { note } }),
  loadDefaults: () =>
    api<PdiDefaultReport>(`${BASE}/library/defaults`, { method: 'POST', body: {} }),
};

const projects = (vesselId?: string | null) =>
  api<DrydockProjects>(`${BASE}/projects${vesselId ? `?vesselId=${enc(vesselId)}` : ''}`);

/**
 * Dry dock projeleri (ADR 0129 §5): gemi verisi pakette gelir; ofis yalnız görür, gemiye göre
 * süzer.
 */
export const officeDrydockProjectSource: DrydockProjectSource = {
  scope: 'office',
  load: projects,
  project: (id) => api<DrydockProject>(`${BASE}/projects/${enc(id)}`),
  inspection: (id) => api<PdiInspection>(`${BASE}/inspections/${enc(id)}`),
  // Faz 4: kapanış önizlemesi ofiste salt okunur
  closeout: (id) => api<DrydockCloseout>(`${BASE}/projects/${enc(id)}/closeout`),
  // Faz 5 (ADR 0129 Ek Faz 5): denetim izi ve raporların markası
  audit: (id) => api<DrydockAudit>(`${BASE}/projects/${enc(id)}/audit`),
  brand: officeBrand,
};

/**
 * Pre-Docking denetimi (ADR 0129 Ek Faz 2): gemi verisi, ofiste salt okunur (yazma işlemi yok);
 * kanıt ekleri gemiden yüklenince açılır.
 */
export const officePdiInspectionSource: PdiInspectionSource = {
  scope: 'office',
  projects,
  work: (id) => api<PdiInspectionWork>(`${BASE}/inspections/${enc(id)}`),
  attachments: (resultId) => officeApi.pmsAttachments(resultId, 'pdi_result'),
  attachmentUrl: (id) => `/api/office/attachments/${enc(id)}`,
  brand: officeBrand,
};

/**
 * Bulgu ve işler (ADR 0129 Ek Faz 3): gemi verisi, ofiste salt okunur; ofisin tek yazması işin
 * kararıdır (Superintendent; onay `approve`, iade ve ret `return`).
 */
export const officeDrydockScopeSource: DrydockScopeSource = {
  scope: 'office',
  brand: officeBrand,
  load: (projectId) => api<DrydockScope>(`${BASE}/projects/${enc(projectId)}/scope`),
  jobs: ({ vesselId, projectId }) => {
    const query = new URLSearchParams({
      ...(vesselId ? { vesselId } : {}),
      ...(projectId ? { projectId } : {}),
    });
    return api<DrydockJobList>(`${BASE}/jobs${query.size > 0 ? `?${query}` : ''}`);
  },
  decide: ({ jobId, decision, password }) =>
    api<DrydockScope>(`${BASE}/jobs/${enc(jobId)}/decision`, {
      method: 'POST',
      body: { decision, ...(password ? { password } : {}) },
    }),
  // Faz 4 (ADR 0129 Ek Faz 4): Superintendent'ın doğrulaması (`approve`) ya da iadesi (`return`)
  verifyJob: ({ jobId, verification, password }) =>
    api<DrydockScope>(`${BASE}/jobs/${enc(jobId)}/verification`, {
      method: 'POST',
      body: { verification: { ...verification }, ...(password ? { password } : {}) },
    }),
  verifyFinding: ({ findingId, verification, password }) =>
    api<DrydockScope>(`${BASE}/findings/${enc(findingId)}/verification`, {
      method: 'POST',
      body: { verification: { ...verification }, ...(password ? { password } : {}) },
    }),
};

/** Ayarlar ▸ Dry dock (sahip kararı 2026-10-26): herkes okur; `settings.edit` değiştirir. */
export function officeDrydockSettingsSource(canEdit: boolean): DrydockSettingsSource {
  return {
    load: () => api<DrydockSettings>('/office/settings/drydock'),
    ...(canEdit
      ? {
          save: (rowVersion: number | null, selfReview: boolean) =>
            api<DrydockSettings>('/office/settings/drydock', {
              method: 'PATCH',
              body: { rowVersion, selfReview },
            }),
        }
      : {}),
  };
}
